import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';

const CaseReviewPage = () => {
  const { id: caseId } = useParams();
  const navigate = useNavigate();

  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [modifications, setModifications] = useState({
    amount: '',
    paymentTerms: '',
    nonMonetary: '',
  });

  useEffect(() => {
    const fetchCaseDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('Fetching case details for:', caseId);

        // Get token from localStorage or context
        const token = localStorage.getItem('attorneyToken');
        console.log('Token exists:', !!token);

        const data = await api.getCaseDetails(caseId, token);
        console.log('Case data received:', data);

        setCaseData(data);

        // Pre-fill modifications if at review point 3
        if (data.reviewPoint === 3 && data.settlement) {
          setModifications({
            amount: data.settlement.amount || '',
            paymentTerms: data.settlement.paymentTerms || '',
            nonMonetary: data.settlement.nonMonetary || '',
          });
        }

        console.log('Case data set successfully');
      } catch (err) {
        console.error('Error fetching case details:', err);
        setError(err.message || 'Failed to load case details');
      } finally {
        console.log('Setting loading to false');
        setLoading(false);
      }
    };

    if (caseId) {
      fetchCaseDetails();
    }
  }, [caseId]);

  const handleApprove = async () => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem('attorneyToken');
      await api.approveCase(caseId, token, {
        reviewPoint: caseData.reviewPoint,
        notes
      });
      navigate('/attorney/dashboard');
    } catch (err) {
      setError(err.message || 'Failed to approve case');
    } finally {
      setActionLoading(false);
    }
  };

  const handleModify = async () => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem('attorneyToken');
      await api.approveCase(caseId, token, {
        notes,
        action: 'modify',
        modifications
      });
      navigate('/attorney/dashboard');
    } catch (err) {
      setError(err.message || 'Failed to modify case');
    } finally {
      setActionLoading(false);
      setShowModifyModal(false);
    }
  };

  const handleEscalate = async () => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem('attorneyToken');
      await api.approveCase(caseId, token, { notes, action: 'escalate' });
      navigate('/attorney/dashboard');
    } catch (err) {
      setError(err.message || 'Failed to escalate case');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestModifications = async () => {
    if (!feedback.trim()) {
      setError('Please provide feedback');
      return;
    }

    if (selectedQuestions.length === 0) {
      setError('Please select at least one question that needs improvement');
      return;
    }

    try {
      setActionLoading(true);
      const token = localStorage.getItem('attorneyToken');
      await api.requestModifications(caseId, token, feedback, selectedQuestions);
      navigate('/attorney/dashboard');
    } catch (err) {
      setError(err.message || 'Failed to request modifications');
    } finally {
      setActionLoading(false);
      setShowFeedbackModal(false);
    }
  };

  const toggleQuestionSelection = (questionId) => {
    setSelectedQuestions(prev => {
      if (prev.find(q => q.questionId === questionId)) {
        return prev.filter(q => q.questionId !== questionId);
      } else {
        return [...prev, { questionId, issue: '', suggestion: '' }];
      }
    });
  };

  const updateQuestionFeedback = (questionId, field, value) => {
    setSelectedQuestions(prev =>
      prev.map(q => q.questionId === questionId ? { ...q, [field]: value } : q)
    );
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading case details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="card max-w-md w-full">
          <div className="text-center">
            <div className="text-5xl mb-4">Error</div>
            <h2 className="text-xl font-bold mb-2">Error Loading Case</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => navigate('/attorney/dashboard')}
              className="btn-primary"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!caseData) {
    return null;
  }

  const { reviewPoint, dispute, parties, documents, settlement, settlementZone, previousReviews, signatures, payments } = caseData;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/attorney/dashboard')}
          className="text-primary-500 hover:text-primary-600 mb-4 flex items-center"
        >
          <span className="mr-2">&larr;</span> Back to Dashboard
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">Case Review - Checkpoint {reviewPoint}</h1>
            <p className="text-gray-600">Case ID: {caseId}</p>
          </div>
          <div className="text-right">
            <div className="inline-block px-4 py-2 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-600">Review Point</p>
              <p className="font-bold text-2xl text-primary-600">{reviewPoint}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Case Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="card">
          <h3 className="font-bold mb-2 text-gray-600">Dispute Type</h3>
          <p className="text-lg font-semibold capitalize">{dispute?.type || 'N/A'}</p>
        </div>
        <div className="card">
          <h3 className="font-bold mb-2 text-gray-600">Amount Claimed</h3>
          <p className="text-lg font-semibold">{formatCurrency(dispute?.amountClaimed || 0)}</p>
        </div>
        <div className="card">
          <h3 className="font-bold mb-2 text-gray-600">Status</h3>
          <p className="text-lg font-semibold capitalize">{dispute?.status || 'N/A'}</p>
        </div>
      </div>

      {/* Parties Information */}
      <div className="card mb-6">
        <h2 className="text-xl font-bold mb-4">Parties</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg">
            <h3 className="font-bold mb-2">Party A (Claimant)</h3>
            <p className="text-sm text-gray-700">{parties?.partyA?.name || 'N/A'}</p>
            <p className="text-sm text-gray-600">{parties?.partyA?.email || 'N/A'}</p>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg">
            <h3 className="font-bold mb-2">Party B (Respondent)</h3>
            <p className="text-sm text-gray-700">{parties?.partyB?.name || 'N/A'}</p>
            <p className="text-sm text-gray-600">{parties?.partyB?.email || 'N/A'}</p>
            {reviewPoint >= 2 && (
              <p className="text-sm font-medium mt-2">
                Response Status: <span className="text-green-600">{parties?.partyB?.responded ? 'Responded' : 'Pending'}</span>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Review Point Specific Content */}
      {reviewPoint === 1 && (
        <>
          {/* Review Point 1: Initial Assessment */}
          <div className="card mb-6">
            <h2 className="text-xl font-bold mb-4">Initial Assessment Review - Checkpoint 1</h2>

            {/* Assessment Scoring */}
            {dispute?.assessmentScore !== undefined && (
              <div className="mb-6">
                <h3 className="font-bold mb-3">Assessment Scoring</h3>
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-6 border-2 border-blue-200 mb-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="text-lg font-bold text-gray-800 mb-1">Overall Score</h4>
                      <p className="text-xs text-gray-600">Automated quality assessment</p>
                    </div>
                    <div className="text-right min-w-[120px]">
                      <div className={`text-5xl font-bold tabular-nums ${
                        dispute.assessmentScore >= 70 ? 'text-green-600' :
                        dispute.assessmentScore >= 50 ? 'text-yellow-600' :
                        'text-red-600'
                      }`} style={{letterSpacing: '0.05em'}}>
                        {dispute.assessmentScore}
                      </div>
                      <p className="text-sm text-gray-600">out of 100</p>
                    </div>
                  </div>

                  {/* Score Recommendation */}
                  <div className={`mt-4 p-3 rounded-lg ${
                    dispute.assessmentScore >= 70 ? 'bg-green-100 border border-green-300' :
                    dispute.assessmentScore >= 50 ? 'bg-yellow-100 border border-yellow-300' :
                    'bg-red-100 border border-red-300'
                  }`}>
                    <p className={`text-sm font-medium ${
                      dispute.assessmentScore >= 70 ? 'text-green-800' :
                      dispute.assessmentScore >= 50 ? 'text-yellow-800' :
                      'text-red-800'
                    }`}>
                      {dispute.assessmentScore >= 70 ? '✓ High Quality - Recommended for approval' :
                       dispute.assessmentScore >= 50 ? '⚠ Borderline - Review recommended' :
                       '✗ High Risk - Attorney review required'}
                    </p>
                  </div>
                </div>

                {/* Red Flags */}
                {dispute.assessmentRedFlags && JSON.parse(dispute.assessmentRedFlags).length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <h4 className="font-bold text-red-800 mb-2 flex items-center">
                      <span className="text-xl mr-2">⚠</span>
                      Red Flags Detected ({JSON.parse(dispute.assessmentRedFlags).length})
                    </h4>
                    <ul className="space-y-2">
                      {JSON.parse(dispute.assessmentRedFlags).map((flag, idx) => (
                        <li key={idx} className="text-sm text-red-700 flex items-start">
                          <span className="mr-2">•</span>
                          <div>
                            <span className="font-medium capitalize">{flag.type.replace(/_/g, ' ')}:</span> {flag.description}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Evidence Quality */}
            <div className="mb-6">
              <h3 className="font-bold mb-3">Evidence Submitted</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {documents && documents.length > 0 ? (
                  documents.map((doc, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-lg flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{doc.filename}</p>
                        <p className="text-xs text-gray-600 capitalize">{doc.category}</p>
                      </div>
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-500 text-sm hover:underline"
                      >
                        View
                      </a>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-600 text-sm">No documents uploaded</p>
                )}
              </div>
            </div>

            {/* Assessment Results */}
            <div className="mb-6">
              <h3 className="font-bold mb-3">Party A Assessment Responses</h3>
              <div className="bg-blue-50 rounded-lg p-4">
                {parties?.partyA?.assessments && parties.partyA.assessments.length > 0 ? (
                  <div className="space-y-4">
                    {parties.partyA.assessments.map((qa, index) => (
                      <div key={index} className="bg-white rounded-lg p-3">
                        <p className="text-sm font-medium text-gray-800 mb-1">Question: {qa.question_id}</p>
                        <p className="text-sm text-gray-700">{qa.answer_value}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">Assessment not completed</p>
                )}
              </div>
            </div>

            {/* Pattern Match */}
            {dispute?.patternMatch && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="font-bold mb-2 text-green-800">Pattern Match Detected</h3>
                <p className="text-sm text-gray-700 mb-2">{dispute.patternMatch.description}</p>
                <div className="flex items-center text-sm">
                  <span className="text-green-600 font-medium mr-2">Confidence:</span>
                  <span>{dispute.patternMatch.confidence}%</span>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {reviewPoint === 2 && (
        <>
          {/* Review Point 2: Response Analysis */}
          <div className="card mb-6">
            <h2 className="text-xl font-bold mb-4">Response Analysis Review</h2>

            {/* Party B Response Status */}
            <div className="mb-6">
              <h3 className="font-bold mb-3">Party B Response</h3>
              {parties?.partyB?.responded ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center mb-2">
                    <span className="text-green-500 text-xl mr-2">&#10003;</span>
                    <span className="font-medium text-green-800">Response Received</span>
                  </div>
                  <p className="text-sm text-gray-700">Responded on: {formatDate(parties.partyB.respondedAt)}</p>
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center mb-2">
                    <span className="text-yellow-500 text-xl mr-2">&#9888;</span>
                    <span className="font-medium text-yellow-800">Awaiting Response</span>
                  </div>
                  <p className="text-sm text-gray-700">Party B has not yet responded</p>
                </div>
              )}
            </div>

            {/* Party B Assessment */}
            {parties?.partyB?.assessments && parties.partyB.assessments.length > 0 && (
              <div className="mb-6">
                <h3 className="font-bold mb-3">Party B Assessment</h3>
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="space-y-4">
                    {parties.partyB.assessments.map((qa, index) => (
                      <div key={index} className="bg-white rounded-lg p-3">
                        <p className="text-sm font-medium text-gray-800 mb-1">Question: {qa.question_id}</p>
                        <p className="text-sm text-gray-700">{qa.answer_value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Good Faith Indicators */}
            <div className="mb-6">
              <h3 className="font-bold mb-3">Good Faith Indicators</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <span className={`mr-2 ${parties?.partyB?.assessments?.length > 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {parties?.partyB?.assessments?.length > 0 ? '✓' : '✗'}
                    </span>
                    <span className="text-sm">Timely Response</span>
                  </div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <span className={`mr-2 ${parties?.partyB?.assessments?.length > 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {parties?.partyB?.assessments?.length > 0 ? '✓' : '✗'}
                    </span>
                    <span className="text-sm">Complete Assessment</span>
                  </div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <span className={`mr-2 ${settlementZone?.exists ? 'text-green-500' : 'text-yellow-500'}`}>
                      {settlementZone?.exists ? '✓' : '⚠'}
                    </span>
                    <span className="text-sm">Settlement Zone {settlementZone?.exists ? 'Found' : 'Pending'}</span>
                  </div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <span className={`mr-2 ${parties?.partyB?.settlementParams ? 'text-green-500' : 'text-yellow-500'}`}>
                      {parties?.partyB?.settlementParams ? '✓' : '⚠'}
                    </span>
                    <span className="text-sm">Settlement Parameters Provided</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Settlement Parameters (Attorney View) */}
            {(parties?.partyA?.settlementParams || parties?.partyB?.settlementParams) && (
              <div className="mb-6">
                <h3 className="font-bold mb-3">Settlement Parameters (Confidential - Attorney View)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Party A Parameters */}
                  {parties?.partyA?.settlementParams && (
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h4 className="font-bold text-sm mb-3 text-blue-800">Party A (Claimant) Parameters</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-700">Minimum Acceptable:</span>
                          <span className="font-medium">{formatCurrency(parties.partyA.settlementParams.minimumAcceptable)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Ideal Amount:</span>
                          <span className="font-medium">{formatCurrency(parties.partyA.settlementParams.idealAmount)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Timeline:</span>
                          <span className="font-medium">{parties.partyA.settlementParams.timelineDays} days</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Relationship Important:</span>
                          <span className="font-medium">{parties.partyA.settlementParams.relationshipImportant ? 'Yes' : 'No'}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Party B Parameters */}
                  {parties?.partyB?.settlementParams && (
                    <div className="bg-purple-50 rounded-lg p-4">
                      <h4 className="font-bold text-sm mb-3 text-purple-800">Party B (Respondent) Parameters</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-700">Maximum Payable:</span>
                          <span className="font-medium">{formatCurrency(parties.partyB.settlementParams.maximumPayable)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Payment Plan OK:</span>
                          <span className="font-medium">{parties.partyB.settlementParams.paymentPlanAcceptable ? 'Yes' : 'No'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Timeline:</span>
                          <span className="font-medium">{parties.partyB.settlementParams.timelineDays} days</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Relationship Important:</span>
                          <span className="font-medium">{parties.partyB.settlementParams.relationshipImportant ? 'Yes' : 'No'}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Settlement Zone Overlap */}
            {settlementZone?.exists && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="font-bold mb-3 text-green-800">Settlement Zone Analysis</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600 mb-1">Overlap Range</p>
                    <p className="font-medium">{formatCurrency(settlementZone.amountMin)} - {formatCurrency(settlementZone.amountMax)}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 mb-1">Zone Quality</p>
                    <p className="font-medium capitalize">{settlementZone.quality || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 mb-1">Timeline Compatible</p>
                    <p className="font-medium">{settlementZone.timelineCompatible ? 'Yes' : 'No'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {reviewPoint === 3 && (
        <>
          {/* Settlement Parameters (Attorney View) */}
          {(parties?.partyA?.settlementParams || parties?.partyB?.settlementParams) && (
            <div className="card mb-6">
              <h2 className="text-xl font-bold mb-4">Settlement Parameters (Confidential - Attorney View)</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Party A Parameters */}
                {parties?.partyA?.settlementParams && (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="font-bold text-sm mb-3 text-blue-800">Party A (Claimant) Parameters</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-700">Minimum Acceptable:</span>
                        <span className="font-medium">{formatCurrency(parties.partyA.settlementParams.minimumAcceptable)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Ideal Amount:</span>
                        <span className="font-medium">{formatCurrency(parties.partyA.settlementParams.idealAmount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Timeline:</span>
                        <span className="font-medium">{parties.partyA.settlementParams.timelineDays} days</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Relationship Important:</span>
                        <span className="font-medium">{parties.partyA.settlementParams.relationshipImportant ? 'Yes' : 'No'}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Party B Parameters */}
                {parties?.partyB?.settlementParams && (
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h4 className="font-bold text-sm mb-3 text-purple-800">Party B (Respondent) Parameters</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-700">Maximum Payable:</span>
                        <span className="font-medium">{formatCurrency(parties.partyB.settlementParams.maximumPayable)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Payment Plan OK:</span>
                        <span className="font-medium">{parties.partyB.settlementParams.paymentPlanAcceptable ? 'Yes' : 'No'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Timeline:</span>
                        <span className="font-medium">{parties.partyB.settlementParams.timelineDays} days</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Relationship Important:</span>
                        <span className="font-medium">{parties.partyB.settlementParams.relationshipImportant ? 'Yes' : 'No'}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Settlement Zone Overlap */}
              {settlementZone?.exists && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                  <h3 className="font-bold mb-3 text-green-800">Settlement Zone Analysis</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600 mb-1">Overlap Range</p>
                      <p className="font-medium">{formatCurrency(settlementZone.amountMin)} - {formatCurrency(settlementZone.amountMax)}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 mb-1">Zone Quality</p>
                      <p className="font-medium capitalize">{settlementZone.quality || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 mb-1">Timeline Compatible</p>
                      <p className="font-medium">{settlementZone.timelineCompatible ? 'Yes' : 'No'}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Review Point 3: Settlement Review */}
          <div className="card mb-6">
            <h2 className="text-xl font-bold mb-4">Settlement Agreement Review</h2>

            {settlement ? (
              <>
                {/* AI-Generated Settlement */}
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 mb-6 border-2 border-blue-200">
                  <div className="flex items-center mb-4">
                    <span className="text-3xl mr-3">&#129302;</span>
                    <h3 className="text-xl font-bold">AI-Generated Settlement Proposal</h3>
                  </div>

                  <div className="space-y-4">
                    {/* Settlement Amount */}
                    <div className="bg-white rounded-lg p-4">
                      <h4 className="font-bold mb-2 text-gray-700">Settlement Amount</h4>
                      <p className="text-2xl font-bold text-primary-600">{formatCurrency(settlement.amount)}</p>
                      <p className="text-sm text-gray-600 mt-1">
                        This amount falls within the overlapping range of both parties' acceptable parameters.
                      </p>
                    </div>

                    {/* Payment Structure */}
                    <div className="bg-white rounded-lg p-4">
                      <h4 className="font-bold mb-2 text-gray-700">Payment Structure</h4>
                      {settlement.structure && (
                        <>
                          {settlement.structure.type === 'lump_sum' ? (
                            <p className="text-gray-800">
                              Lump sum payment due within {settlement.structure.dueInDays} days
                            </p>
                          ) : settlement.structure.type === 'installment' ? (
                            <div className="text-gray-800 space-y-2">
                              <p>Installment plan:</p>
                              <ul className="list-disc list-inside ml-4">
                                <li>Initial payment: {formatCurrency(settlement.structure.initial)}</li>
                                {settlement.structure.installments && settlement.structure.installments.map((inst, idx) => (
                                  <li key={idx}>
                                    {formatCurrency(inst.amount)} due in {inst.dueInDays} days
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : (
                            <p className="text-gray-800">{JSON.stringify(settlement.structure)}</p>
                          )}
                        </>
                      )}
                    </div>

                    {/* Terms */}
                    {settlement.terms && settlement.terms.length > 0 && (
                      <div className="bg-white rounded-lg p-4">
                        <h4 className="font-bold mb-2 text-gray-700">Settlement Terms</h4>
                        <ul className="list-disc list-inside space-y-1">
                          {settlement.terms.map((term, index) => (
                            <li key={index} className="text-sm text-gray-700 capitalize">
                              {term.replace(/_/g, ' ')}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Status */}
                    <div className="bg-white rounded-lg p-4">
                      <h4 className="font-bold mb-2 text-gray-700">Status</h4>
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          settlement.attorneyApproved
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {settlement.attorneyApproved ? 'Approved by Attorney' : 'Pending Attorney Approval'}
                        </span>
                        <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 capitalize">
                          {settlement.status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modify Settlement Button */}
                <div className="mb-6">
                  <button
                    onClick={() => setShowModifyModal(true)}
                    className="btn-secondary w-full"
                  >
                    Modify Settlement Terms
                  </button>
                </div>
              </>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-gray-700">Settlement proposal is being generated...</p>
              </div>
            )}
          </div>

        </>
      )}

      {/* Settlement Details - Show whenever settlement exists */}
      {settlement && (
        <div className="card mb-6">
          <h2 className="text-xl font-bold mb-4">Settlement Agreement Details</h2>
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 border-2 border-blue-200">
            <div className="space-y-4">
              {/* Settlement Amount */}
              <div className="bg-white rounded-lg p-4">
                <h4 className="font-bold mb-2 text-gray-700">Settlement Amount</h4>
                <p className="text-2xl font-bold text-primary-600">{formatCurrency(settlement.amount)}</p>
              </div>

              {/* Payment Structure */}
              <div className="bg-white rounded-lg p-4">
                <h4 className="font-bold mb-2 text-gray-700">Payment Structure</h4>
                {settlement.structure && (
                  <>
                    {settlement.structure.type === 'lump_sum' ? (
                      <p className="text-gray-800">
                        Lump sum payment due within {settlement.structure.dueInDays} days
                      </p>
                    ) : settlement.structure.type === 'installment' ? (
                      <div className="text-gray-800 space-y-2">
                        <p>Installment plan:</p>
                        <ul className="list-disc list-inside ml-4">
                          <li>Initial payment: {formatCurrency(settlement.structure.initial)}</li>
                          {settlement.structure.installments && settlement.structure.installments.map((inst, idx) => (
                            <li key={idx}>
                              {formatCurrency(inst.amount)} due in {inst.dueInDays} days
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <p className="text-gray-800">{JSON.stringify(settlement.structure)}</p>
                    )}
                  </>
                )}
              </div>

              {/* Terms */}
              {settlement.terms && settlement.terms.length > 0 && (
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-bold mb-2 text-gray-700">Settlement Terms</h4>
                  <ul className="list-disc list-inside space-y-1">
                    {settlement.terms.map((term, index) => (
                      <li key={index} className="text-sm text-gray-700 capitalize">
                        {term.replace(/_/g, ' ')}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Status */}
              <div className="bg-white rounded-lg p-4">
                <h4 className="font-bold mb-2 text-gray-700">Status</h4>
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    settlement.attorneyApproved
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {settlement.attorneyApproved ? 'Approved by Attorney' : 'Pending Attorney Approval'}
                  </span>
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 capitalize">
                    {settlement.status}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settlement Signatures */}
      {settlement && signatures && signatures.length > 0 && (
        <div className="card mb-6">
          <h2 className="text-xl font-bold mb-4">Settlement Signatures</h2>
          <div className="space-y-3">
            {signatures.map((sig, index) => (
              <div key={index} className={`border-l-4 ${sig.partyRole === 'initiator' ? 'border-blue-500 bg-blue-50' : 'border-purple-500 bg-purple-50'} p-4 rounded-r-lg`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-bold text-gray-800">
                      {sig.partyRole === 'initiator' ? '✍️ Party A (Claimant)' : '✍️ Party B (Respondent)'}
                    </p>
                    <p className="text-sm text-gray-700 mt-1">{sig.partyName || sig.partyEmail}</p>
                    <p className="text-xs text-gray-600 mt-1">
                      <strong>Signed:</strong> {formatDate(sig.signedAt)}
                    </p>
                    {sig.ipAddress && (
                      <p className="text-xs text-gray-500 mt-1">
                        IP: {sig.ipAddress}
                      </p>
                    )}
                  </div>
                  <div className="text-green-500 text-3xl">
                    ✓
                  </div>
                </div>
              </div>
            ))}
          </div>

          {signatures.length === 2 && (
            <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <span className="text-green-500 text-2xl mr-3">✅</span>
                <div>
                  <p className="font-bold text-green-800">Both Parties Signed</p>
                  <p className="text-sm text-green-700">Settlement agreement is fully executed</p>
                  {settlement.executedAt && (
                    <p className="text-xs text-green-600 mt-1">
                      Executed on: {formatDate(settlement.executedAt)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {signatures.length === 1 && (
            <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center">
                <span className="text-yellow-500 text-2xl mr-3">⏳</span>
                <div>
                  <p className="font-bold text-yellow-800">Awaiting Second Signature</p>
                  <p className="text-sm text-yellow-700">
                    {signatures[0].partyRole === 'initiator'
                      ? 'Party B (Respondent) has not yet signed'
                      : 'Party A (Claimant) has not yet signed'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Payment Tracking */}
      {settlement && payments && payments.length > 0 && (
        <div className="card mb-6">
          <h2 className="text-xl font-bold mb-4">Payment Tracking</h2>
          <div className="space-y-4">
            {payments.map((payment, index) => (
              <div key={index} className={`border rounded-lg p-4 ${
                payment.status === 'confirmed' || payment.status === 'succeeded'
                  ? 'border-green-300 bg-green-50'
                  : payment.status === 'debtor_indicated_paid'
                  ? 'border-yellow-300 bg-yellow-50'
                  : payment.status === 'declined' || payment.status === 'failed'
                  ? 'border-red-300 bg-red-50'
                  : 'border-gray-300 bg-gray-50'
              }`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-lg">
                        {formatCurrency(payment.amount)}
                      </h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        payment.status === 'confirmed' || payment.status === 'succeeded'
                          ? 'bg-green-100 text-green-800'
                          : payment.status === 'debtor_indicated_paid'
                          ? 'bg-yellow-100 text-yellow-800'
                          : payment.status === 'declined' || payment.status === 'failed'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {payment.status.replace(/_/g, ' ').toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1 capitalize">
                      Type: {payment.paymentType.replace(/_/g, ' ')}
                    </p>
                  </div>
                  <div className="text-right text-sm text-gray-600">
                    <p className="font-mono text-xs">{payment.id}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  {payment.dueDate && (
                    <div>
                      <p className="text-gray-600 font-medium">Due Date</p>
                      <p className="text-gray-800">{new Date(payment.dueDate).toLocaleDateString('en-IE')}</p>
                    </div>
                  )}
                  {payment.paidAt && (
                    <div>
                      <p className="text-gray-600 font-medium">Indicated Paid</p>
                      <p className="text-gray-800">{formatDate(payment.paidAt)}</p>
                    </div>
                  )}
                  {payment.confirmedAt && (
                    <div>
                      <p className="text-green-700 font-medium">✓ Confirmed</p>
                      <p className="text-gray-800">{formatDate(payment.confirmedAt)}</p>
                      {payment.confirmedByName && (
                        <p className="text-xs text-gray-600 mt-1">
                          by {payment.confirmedByName}
                        </p>
                      )}
                    </div>
                  )}
                  {payment.declinedAt && (
                    <div>
                      <p className="text-red-700 font-medium">✗ Declined</p>
                      <p className="text-gray-800">{formatDate(payment.declinedAt)}</p>
                      {payment.declinedByName && (
                        <p className="text-xs text-gray-600 mt-1">
                          by {payment.declinedByName}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {payment.declineReason && (
                  <div className="mt-3 bg-red-100 border border-red-200 rounded p-2">
                    <p className="text-xs text-red-800">
                      <strong>Decline Reason:</strong> {payment.declineReason}
                    </p>
                  </div>
                )}

                {payment.stripePaymentIntentId && (
                  <div className="mt-3 pt-3 border-t border-gray-300">
                    <p className="text-xs text-gray-500">
                      Stripe Payment ID: {payment.stripePaymentIntentId}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Payment Summary */}
          <div className="mt-4 bg-gray-50 border border-gray-300 rounded-lg p-4">
            <h3 className="font-bold mb-2">Payment Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-gray-600">Total Payments</p>
                <p className="font-bold text-lg">{payments.length}</p>
              </div>
              <div>
                <p className="text-gray-600">Confirmed</p>
                <p className="font-bold text-lg text-green-600">
                  {payments.filter(p => p.status === 'confirmed' || p.status === 'succeeded').length}
                </p>
              </div>
              <div>
                <p className="text-gray-600">Pending</p>
                <p className="font-bold text-lg text-yellow-600">
                  {payments.filter(p => p.status === 'pending' || p.status === 'debtor_indicated_paid').length}
                </p>
              </div>
              <div>
                <p className="text-gray-600">Total Amount</p>
                <p className="font-bold text-lg">
                  {formatCurrency(payments.reduce((sum, p) => sum + p.amount, 0))}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Previous Review History */}
      {previousReviews && previousReviews.length > 0 && (
        <div className="card mb-6">
          <h2 className="text-xl font-bold mb-4">Previous Review History</h2>
          <div className="space-y-3">
            {previousReviews.map((review, index) => (
              <div key={index} className="border-l-4 border-primary-500 pl-4 py-2">
                <div className="flex justify-between items-start mb-1">
                  <span className="font-bold">Review Point {review.reviewPoint}</span>
                  <span className="text-sm text-gray-600">{formatDate(review.reviewedAt)}</span>
                </div>
                <p className="text-sm text-gray-700 mb-1">
                  <span className="font-medium">Attorney:</span> {review.reviewedBy}
                </p>
                <p className="text-sm text-gray-700 mb-1">
                  <span className="font-medium">Action:</span> <span className="capitalize">{review.action}</span>
                </p>
                {review.notes && (
                  <p className="text-sm text-gray-600 italic">"{review.notes}"</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Attorney Notes */}
      <div className="card mb-6">
        <h2 className="text-xl font-bold mb-4">Review Notes</h2>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add your review notes here... (optional)"
          className="input w-full min-h-32 resize-y"
        />
        <p className="text-xs text-gray-500 mt-2">
          These notes will be recorded in the case history and may be visible to other attorneys.
        </p>
      </div>

      {/* Action Buttons */}
      <div className="card bg-gray-50">
        <h2 className="text-xl font-bold mb-4">Review Decision</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={handleApprove}
            disabled={actionLoading}
            className="btn-primary flex items-center justify-center"
          >
            <span className="mr-2">&#10003;</span>
            Approve
          </button>
          {reviewPoint === 1 && (
            <button
              onClick={() => setShowFeedbackModal(true)}
              disabled={actionLoading}
              className="bg-yellow-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-yellow-600 transition flex items-center justify-center"
            >
              <span className="mr-2">&#9998;</span>
              Request Modifications
            </button>
          )}
          {reviewPoint === 3 && (
            <button
              onClick={() => setShowModifyModal(true)}
              disabled={actionLoading}
              className="btn-secondary flex items-center justify-center"
            >
              <span className="mr-2">&#9998;</span>
              Modify Settlement
            </button>
          )}
          <button
            onClick={handleEscalate}
            disabled={actionLoading}
            className="bg-orange-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-600 transition flex items-center justify-center"
          >
            <span className="mr-2">&#8593;</span>
            Escalate
          </button>
        </div>
        <p className="text-xs text-gray-600 mt-4 text-center">
          {reviewPoint === 1
            ? 'Approve to proceed with invitation. Request Modifications to ask Party A for improvements. Escalate for senior review.'
            : 'Approving will move the case to the next stage. Escalating will flag the case for senior review.'}
        </p>
      </div>

      {/* Modify Settlement Modal */}
      {showModifyModal && reviewPoint === 3 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto p-6">
            <h2 className="text-2xl font-bold mb-4">Modify Settlement Terms</h2>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block font-medium mb-2">Settlement Amount (EUR)</label>
                <input
                  type="number"
                  value={modifications.amount}
                  onChange={(e) => setModifications({ ...modifications, amount: e.target.value })}
                  className="input"
                  placeholder="Enter amount"
                />
              </div>

              <div>
                <label className="block font-medium mb-2">Payment Terms</label>
                <textarea
                  value={modifications.paymentTerms}
                  onChange={(e) => setModifications({ ...modifications, paymentTerms: e.target.value })}
                  className="input min-h-24 resize-y"
                  placeholder="Describe payment terms..."
                />
              </div>

              <div>
                <label className="block font-medium mb-2">Non-Monetary Terms (Optional)</label>
                <textarea
                  value={modifications.nonMonetary}
                  onChange={(e) => setModifications({ ...modifications, nonMonetary: e.target.value })}
                  className="input min-h-24 resize-y"
                  placeholder="Describe any non-monetary terms..."
                />
              </div>
            </div>

            <div className="flex gap-4">
              <button
                onClick={handleModify}
                disabled={actionLoading}
                className="btn-primary flex-1"
              >
                Save Modifications
              </button>
              <button
                onClick={() => setShowModifyModal(false)}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request Modifications Modal - Checkpoint 1 */}
      {showFeedbackModal && reviewPoint === 1 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-screen overflow-y-auto p-6">
            <h2 className="text-2xl font-bold mb-4">Request Modifications from Party A</h2>

            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-gray-700">
                Party A will receive an email with your feedback and instructions to improve specific responses.
                They can edit their answers and resubmit for your review.
              </p>
            </div>

            {/* General Feedback */}
            <div className="mb-6">
              <label className="block font-medium mb-2">General Feedback (Required)</label>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                className="input min-h-32 resize-y"
                placeholder="Explain what improvements are needed and why..."
              />
            </div>

            {/* Select Questions to Improve */}
            <div className="mb-6">
              <h3 className="font-bold mb-3">Select Questions That Need Improvement</h3>
              <p className="text-sm text-gray-600 mb-4">
                Check the questions below that Party A should revise. You can provide specific guidance for each.
              </p>
              <div className="space-y-4">
                {parties?.partyA?.assessments && parties.partyA.assessments.length > 0 ? (
                  parties.partyA.assessments.map((qa, index) => {
                    const isSelected = selectedQuestions.find(q => q.questionId === qa.question_id);
                    return (
                      <div key={index} className={`border rounded-lg p-4 ${isSelected ? 'border-yellow-500 bg-yellow-50' : 'border-gray-200'}`}>
                        <div className="flex items-start mb-3">
                          <input
                            type="checkbox"
                            checked={!!isSelected}
                            onChange={() => toggleQuestionSelection(qa.question_id)}
                            className="mt-1 mr-3"
                          />
                          <div className="flex-1">
                            <p className="font-medium text-gray-900 mb-1">Question: {qa.question_id}</p>
                            <p className="text-sm text-gray-700 mb-2">Current answer: "{qa.answer_value}"</p>
                          </div>
                        </div>

                        {isSelected && (
                          <div className="ml-8 space-y-3 mt-3 pt-3 border-t border-yellow-200">
                            <div>
                              <label className="block text-sm font-medium mb-1">Issue with this answer:</label>
                              <input
                                type="text"
                                value={isSelected.issue || ''}
                                onChange={(e) => updateQuestionFeedback(qa.question_id, 'issue', e.target.value)}
                                className="input text-sm"
                                placeholder="e.g., 'Insufficient detail' or 'Unclear timeline'"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-1">Suggestion for improvement:</label>
                              <input
                                type="text"
                                value={isSelected.suggestion || ''}
                                onChange={(e) => updateQuestionFeedback(qa.question_id, 'suggestion', e.target.value)}
                                className="input text-sm"
                                placeholder="e.g., 'Please provide specific dates and amounts'"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <p className="text-gray-600 text-sm">No assessment responses available</p>
                )}
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <div className="flex gap-4">
              <button
                onClick={handleRequestModifications}
                disabled={actionLoading}
                className="btn-primary flex-1"
              >
                {actionLoading ? 'Sending...' : 'Send Feedback to Party A'}
              </button>
              <button
                onClick={() => {
                  setShowFeedbackModal(false);
                  setError(null);
                }}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer Info */}
      <div className="mt-8 text-center text-xs text-gray-400">
        <p>
          &#128274; Attorney-reviewed process • &#9876; Confidential case information
        </p>
      </div>
    </div>
  );
};

export default CaseReviewPage;
